import { makeAutoObservable } from "mobx"
import { observer } from "mobx-react-lite"
import { useMemo } from "react"
import { notUndefined } from "reusable/utils/utils"
import { z } from "zod"
import FormErrorHandler, { FieldErrorHandler } from "./FormErrorHandler"
import { notNull } from "@gap/utils/utils"
import useStore from "reusable/mobx-store-utils/useStore"
import FormHandlerStore, { FormHandlerStoreProvider } from "./FormHandlerStore"
import Debug from "reusable/utils/debug"

/**
 * ## Form Handler
 * creates a form handler object that can be used to bind inputs to a mobx store
 * ### Params and Returns
 * @param store the mobx store in which the desired fields are defined
 * @param configs the fields to be bound to the form
 * @param extraConfig Extra config. Houses a global onValueChange that triggers on any form field update
 * @returns an object with the same keys as the fields object, each key containing a bindInput function to be used in the react component
 * @example
 *
 * const localStore = {
 *   name: "bob",
 *   age: 20,
 *   doSomething: () => { ... }
 * }
 *
 * const handler = formHandler(localStore, {
 *  name: { type: 'string', onValueChange: () => console.log("do something"), },
 *  age: { type: 'number' },
 * } satisfies FormConfigParameter)
 */
export function formHandler<
   TStore extends StoreType,
   TConfig extends NotArray<Pick<Record<keyof TStore, Config>, TKeys>> & FormHandlerConfig,
   TKeys extends keyof TConfig
>(store: TStore, fieldNames: TConfig, extraConfig: { onValueChange?: () => void } = {}) {

   type HandlerType = { [K in keyof TConfig]: FormHandlerReturn<TConfig[K]> }

   const handlers = {} as HandlerType
   const errorHandler = new FormErrorHandler<TConfig>()
   const validator = formValidator({ store, fieldNames, handlers, errorHandler })

   for (const fieldName in fieldNames) {
      const config = fieldNames[fieldName]
      if (!config) {
         throw new Error("config is null")
      }
      const fieldErrorHandler = errorHandler.getFieldErrorHandler(fieldName)
      const fieldHandler = new FormFieldHandler({ store, fieldName, config, errorHandler: fieldErrorHandler, onValueChange: extraConfig.onValueChange })
      handlers[fieldName] = fieldHandler
   }

   return { ...handlers, errorHandler, validate: validator }
}

function formValidator
   <
      TStore extends StoreType,
      TConfig extends NotArray<Pick<Record<keyof TStore, Config>, TKeys>> & FormHandlerConfig,
      TKeys extends keyof TConfig,
      THandler extends Record<string, FormHandlerReturn<TConfig[string]>>,
   >(params: {
      store: TStore,
      fieldNames: Record<string, Config>,
      handlers: THandler,
      errorHandler: FormErrorHandler<Record<string, Config>>
   }) {

   /**
    * ### Form validator
    * This function is attached to the new formhandler. You can quickly call validate on it with the given dto schema and it will validate it for you, and set errors if found
    * @param dtoSchema DTO schema detailing your schema structure
    * @return boolean indicating if the form is valid
    * @example
    * const fields = { firstName: '' }
    * const myFormHandler = formHandler(fields, {
    *     firstName: { type: 'string' },
    * })
    * const success = myFormHandler.validate(OrgEditClientDtoSchema)
    * if (success) {
    *     saveFields(fields)
    * }
    */
   return function validate(dtoSchema: z.AnyZodObject | z.ZodEffects<any>, options?: { dontSetErrors?: boolean }) {
      params.errorHandler.reset()
      let isSuccessful = true;

      const fields: Record<string, SpecificConfigsValue> = {}
      for (const field in params.fieldNames) {
         // Dates are stored as strings in the db, so we need to convert them to iso strings before validating
         if (params.store[field] instanceof Date) {
            // Users are able to edit date fields i.e. 12 MMMM 2023, so check we have a valid date
            const parsedDate = Date.parse(params.store[field] as any)
            if (!Number.isNaN(parsedDate)) {
               fields[field] = params.store[field].toISOString() as any
            } else {
               fields[field] = params.store[field].toString() as any
            }
         } else if (params.fieldNames[field].type === 'number') {
            const fieldConfig = params.fieldNames[field]

            fields[field] = Number(params.store[field]) as unknown as SpecificConfigsValue
            const value = Number(params.store[field])
            if ('optional' in fieldConfig) {
               if (fieldConfig.optional && isNaN(value)) {
                  fields[field] = undefined as unknown as SpecificConfigsValue
               } else {
                  fields[field] = value as unknown as SpecificConfigsValue
               }
            }
         } else {
            fields[field] = params.store[field];
         }
      }

      const result = dtoSchema.safeParse(fields)
      // Filter out non existing fields errors
      if (!result.success) {
         for (const singleError of result.error.errors) {
            const fieldName = singleError.path[0]
            // Only validate fields that has been set in the config
            if (params.handlers[fieldName] && params.fieldNames[fieldName]) {
               isSuccessful = false
               if (!options?.dontSetErrors) {
                  params.handlers[fieldName].setError(singleError.message)
               }

               Debug.log.formhandler(`[DEV ONLY LOG] - fieldname ${fieldName} failed, reason: ${singleError.message}`)
            }
         }
      }
      return isSuccessful
   }
}

/**
 * ## Form Field Handler
 * creates a field handler object specific to a field, providing a function to bind the field to an input
 */
class FormFieldHandler
   <TStore extends StoreType, TFormHandlerConfig extends Config>
   implements FormHandlerReturn<TFormHandlerConfig> {

   sp
   lastCharDot = false

   constructor(sp: { store: TStore, fieldName: keyof TStore, config: TFormHandlerConfig, errorHandler: FieldErrorHandler, onValueChange?: () => void }) {
      this.sp = sp
      makeAutoObservable(this)
   }

   get hasError() {
      return this.sp.errorHandler.hasError
   }

   get error() {
      return this.sp.errorHandler.error
   }

   get inputProps() {
      const value = this.sp.store?.[this.sp.fieldName]
      const config = this.sp.config
      const { type } = config
      const errorProps = this.hasError ? { error: true, helperText: this.error } : { error: undefined, helperText: undefined }

      type DefaultPropsType<T extends SpecificConfigsValue | SpecificConfigsOptionalValue> = BindInputPropsProps<T> & { type: typeof config.type }
      const defaultReturn = {
         type,
         value: this.formatElementValue(value),
         onChange: this.onChange,
         ...errorProps
      }

      if (type === 'boolean') {
         type PropsType = BindInputPropsProps<ConfigBoolean> & { type: typeof config.type }
         return {
            type,
            checked: value,
            onChange: this.onChange,
            ...errorProps
         } as PropsType
      } else if (type === 'date') {
         return defaultReturn as DefaultPropsType<ConfigDate>
      } else if (type === 'number') {
         return defaultReturn as DefaultPropsType<ConfigNumber>
      } else if (type === 'numeric-string') {
         return defaultReturn as DefaultPropsType<ConfigNumericString>
      } else if (type === 'string') {
         return defaultReturn as DefaultPropsType<ConfigString>
      } else if (type === 'any') {
         return defaultReturn as DefaultPropsType<ConfigAny>
      } else if (type === 'rich-text-editor') {
         return defaultReturn as DefaultPropsType<ConfigRichTextEditor>
      } else if (type === 'array') {
         return defaultReturn as DefaultPropsType<ConfigArray>
      }

      throw Error("invalid config type")
   }

   get thousandsSeparator() {
      const config = this.sp.config
      if (config.type === 'number') {
         return config.thousandsSeparator || ','
      }
      return ''
   }

   get decimalsSeparator() {
      const config = this.sp.config
      if (config.type === 'number') {
         return config.decimalsSeparator || '.'
      }
      return ''
   }

   get maxDecimalPlaces() {
      const config = this.sp.config
      if (config.type === 'number') {
         return config.maxDecimalPlaces || 2
      }
      return 2
   }

   get value() {
      return this.sp.store[this.sp.fieldName]
   }

   bindInput: BindInput<TFormHandlerConfig> = bindInputProps => {
      const func = observer(bindInputProps)
      return <InputWrapper handler={this} bindInputProps={func} />
   }

   resetError() {
      this.sp.errorHandler.reset()
   }

   addError(e: string) {
      this.sp.errorHandler.addError(e)
   }

   setError(e: string) {
      this.sp.errorHandler.setError(e)
   }

   setValue(value: any) {
      (this.sp.store[this.sp.fieldName] as any) = value
      this.sp.config.onValueChange?.()
   }

   getValue() {
      return this.sp.store[this.sp.fieldName]
   }

   onChange = (e: any) => {
      this.lastCharDot = false
      const { config } = this.sp
      let value: any

      // if it is a boolean, the value is the checked property of the target.
      if (config.type === 'boolean') {
         value = e.target ? e.target.checked : e
      } else if (config.type === 'date') {
         let newValue: string | Date | null = e?.target ? e.target.value : e || null
         if (!!newValue && config.useStartOfDay) {
            const startOfDay = newValue instanceof Date ? newValue : new Date(newValue)
            startOfDay.setHours(0)
            newValue = startOfDay
         }
         value = newValue
      } else {
         value = e?.target ? e.target.value : e
      }

      value = this.parseElementValue(value)

      if (!this.validateValue(value)) {
         return
      }

      if (value !== (this.sp.store[this.sp.fieldName] as any)) {
         (this.sp.store[this.sp.fieldName] as any) = value
         this.sp.config.onValueChange?.()
         this.sp.onValueChange?.()
      }


   }

   validateValue(value: any) {
      const config = this.sp.config
      if (config.type === 'number') {
         if (value !== undefined && value !== null && value !== '' && value !== '-') {
            if (isNaN(value)) {
               return false
            }
            if (
               config.maxDecimalPlaces !== undefined &&
               this.countDecimals(value) > config.maxDecimalPlaces
            ) {
               return false
            }
            if (
               config.maxIntegerLength !== undefined &&
               this.countIntegerLength(value) > config.maxIntegerLength
            ) {
               return false
            }
            if (config.maxDecimalPlaces === 0 && (value || '').includes('.')) {
               return false
            }
         } else {
            if (value === '-') {
               return false
            }
         }
      }
      return true
   }

   /** parses the value that comes from the html input, that will go to the state */
   parseElementValue(value: any) {
      const config = this.sp.config
      if (config.type === 'number') {
         if (!value) {
            value = config.min ? `${config.min}` : '0'
         } else {
            const valueAsNumber = parseFloat(value)

            if ('min' in config && valueAsNumber < notNull(config.min)) {
               value = `${config.min}`
            }
            if ('max' in config && valueAsNumber > notNull(config.max)) {
               value = `${config.max}`
            }

            value = (value as string).toString().trim()

            if (value === '.') {
               value = '0.'
            } else if (value === '0.') {
               // keep it like that
            } else {
               if (value.length > 1 && value[0] === '0' && value[1] !== '.') {
                  value = value.replace('0', '')
               }
               value = value.replace(new RegExp(this.thousandsSeparator, 'g'), '')
               const decSep = this.decimalsSeparator
               value = value.replace(decSep, '.')
            }
         }
         if (value[value.length - 1] === '.') {
            this.lastCharDot = true
         }

      }
      return value
   }

   /** formats the value that comes from the state, that will go into the input element */
   formatElementValue(value: any) {
      const config = this.sp.config
      if (config.type === 'number') {
         if (value === undefined || value === null) {
            value = ''
         } else {
            value = value.toString().trim()
            if (this.lastCharDot) {
               value = value + '.'
            }

            if (value === '-' || value === '0.') {
               // just uses value as is
            } else if (value === '.') {
               value = '0.'
            } else {
               const split = value.split('.')
               const intValue = split[0].replace(/\B(?=(\d{3})+(?!\d))/g, this.thousandsSeparator)

               const forceDecimals = !!config.decimalsAlwaysAppearing

               if (split.length > 1) {
                  value = intValue + this.decimalsSeparator
                  if (forceDecimals) {
                     value += split[1].padEnd(this.maxDecimalPlaces, '0')
                  } else {
                     value += split[1]
                  }
               } else {
                  value = intValue
                  if (forceDecimals) {
                     value += '.' + ''.padEnd(this.maxDecimalPlaces, '0')
                  }
               }
            }
         }
      }
      return value
   }

   countDecimals(number: number) {
      if (Math.floor(number.valueOf()) === number.valueOf()) return 0
      const split = number.toString().split('.')
      return (split.length > 1 && split[1].length) || 0
   }

   countIntegerLength(number: number) {
      const split = number.toString().split('.')
      return split[0].length || 0
   }
}

/**
 * ## Input Wrapper
 * a wrapper component that provides the onChange and value props to the input (alongside any other props like error and helperText)
 */
const InputWrapper = observer(
   function InputWrapper<TStore extends StoreType, TFormHandlerConfig extends Config>
      ({ handler, bindInputProps }: { handler: FormFieldHandler<TStore, TFormHandlerConfig>, bindInputProps: BindInputProps<TFormHandlerConfig> }) {

      const formHandlerStore = useStore(sp => new FormHandlerStore(sp), {
         value: () => handler.value,
         getValue: () => store[fieldName],
         setError: (error: string) => errorHandler.setError(error),
         resetErrors: () => errorHandler.reset()
      })

      const { inputProps, sp: { config, errorHandler, fieldName, store } } = handler
      const deps = Object.entries(inputProps).map(d => d[1])
      const { onChange, error, helperText, type: inputType } = inputProps

      type ValueType = BindInputPropsProps<SpecificConfigsValue>['value']
      type CheckedType = BindInputPropsProps<SpecificConfigsChecked>['checked']

      const values: {
         value?: TFormHandlerConfig extends ConfigBoolean ? never : ValueType
         checked?: TFormHandlerConfig extends ConfigBoolean ? CheckedType : never
      } = {}

      if (inputType === 'boolean' && 'checked' in inputProps) {
         (values as { checked: CheckedType }).checked = inputProps.checked
      } else {
         (values as { value: ValueType }).value = inputProps.value
      }

      return useMemo(
         () => {
            switch (inputType) {
               case "boolean":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigBoolean>
                     const props = { onChange, error, helperText, checked: notUndefined(values.checked || config.default || false) }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case "date":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigDate>
                     const props = { onChange, error, helperText, value: values.value || config.default || null }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case "number":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigNumber>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default || 0) }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case "numeric-string":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigNumericString>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default || '') }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case "string":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigString>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default || '') }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case "any":
                  {
                     const Func = bindInputProps as BindInputProps<ConfigAny>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default) }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case 'rich-text-editor':
                  {
                     const Func = bindInputProps as BindInputProps<ConfigRichTextEditor>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default || '') }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
               case 'array':
                  {
                     const Func = bindInputProps as BindInputProps<ConfigArray>
                     const props = { onChange, error, helperText, value: notUndefined(values.value || config.default || []) }
                     return <FormHandlerStoreProvider store={formHandlerStore}><Func {...props} /></FormHandlerStoreProvider>
                  }
            }
         },
         // eslint-disable-next-line react-hooks/exhaustive-deps
         [bindInputProps, ...deps]
      )
   }
)

export type FormHandlerConfig = Record<string, Config>

type StoreType = Record<any, any>

interface ConfigAny {
   type: 'any'
}

export interface ConfigString {
   type: 'string'
}

interface ConfigDate {
   type: 'date'
   useStartOfDay?: boolean
}

interface ConfigNumericString {
   type: 'numeric-string'
}

export interface ConfigBoolean {
   type: 'boolean'
}

interface ConfigRichTextEditor {
   type: 'rich-text-editor'
}

interface ConfigArray {
   type: 'array'
}

export interface ConfigNumber {
   type: 'number'
   decimalsSeparator?: '.' | ','
   maxDecimalPlaces?: number
   maxIntegerLength?: number
   thousandsSeparator?: '.' | ',' | ' ' | ''
   decimalsAlwaysAppearing?: boolean
   optional?: boolean
   min?: number
   max?: number
}

export interface FormHandlerFieldUtils {
   value: any
   getValue: () => any
   setError: FieldErrorHandler['setError']
   resetErrors: FieldErrorHandler['reset']
}

type SpecificConfigsValue =
   ConfigAny |
   ConfigString |
   ConfigNumericString |
   ConfigDate |
   ConfigRichTextEditor |
   ConfigArray

type SpecificConfigsChecked = ConfigBoolean

type SpecificConfigsOptionalValue = ConfigNumber

type Config =
   | (SpecificConfigsValue & { default?: any; onValueChange?: () => void })
   | (SpecificConfigsChecked & { default?: any; onValueChange?: () => void })
   | (SpecificConfigsOptionalValue & { optional?: boolean; default?: any; onValueChange?: () => void });

export type BindInputPropsProps<TSpecificConfig extends Config> =
   {
      onChange: (e: React.ChangeEvent<HTMLInputElement> | React.SyntheticEvent<Element, Event> | string) => void
      helperText?: string
      error?: boolean
   } & (TSpecificConfig extends ConfigBoolean ? { checked: boolean } :
      TSpecificConfig extends ConfigDate ? { value: Date | null } :
      TSpecificConfig extends ConfigArray ? { value: any[] } :
      TSpecificConfig extends ConfigRichTextEditor ? { onChange: (e: string) => void, value: string } :
      TSpecificConfig extends ConfigAny ? { value: any } : { value: string }
   )
type BindInputProps<TSpecificConfig extends Config> = (props: BindInputPropsProps<TSpecificConfig>) => React.ReactElement

type BindInput<TSpecificConfig extends Config> = (bindInputProps: BindInputProps<TSpecificConfig>) => React.ReactElement

export type FormHandlerReturn<TSpecificConfig extends Config> = {
   bindInput: BindInput<TSpecificConfig>

   hasError: FieldErrorHandler['hasError']
   error: FieldErrorHandler['error']
   value: TSpecificConfig['type']
   resetError: FieldErrorHandler['reset']
   addError: FieldErrorHandler['addError']
   setError: FieldErrorHandler['setError']
   setValue: (value: any) => void
   getValue: () => TSpecificConfig['type']
}

type NotArray<T> = T extends object & { length?: never; } ? T : never;
